import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { Txt } from 'components/Txt';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  REMOTE_ASESETUP_SECTIONDELETE,
  REMOTE_ASESETUPLIBRARY_QUESTIONOPEN,
  REMOTE_ASESETUP_SECTIONOPEN,
  REMOTE_ASESETUP_TOGGLESECTIONCOLLAPSED,
  REMOTE_ASESETUP_OPENSECTIONSORDER,
} from 'store/remote/actions';
import {
  selectAseSetupLibrarySection,
  selectAseSetupLibrarySectionIsCollapsed,
  selectAseSetupLibrarySectionSubItems,
} from 'store/remote/selectors';
import { AssessmentQuestionType } from 'types/types';

type Props = {
  id: number;
};

export const SectionListSectionListItem: React.FC<Props> = ({ id }) => {
  const dispatch = useDispatch();
  const section = useSelector((state) =>
    selectAseSetupLibrarySection(state, id),
  );
  const subitems = useSelector((state) =>
    selectAseSetupLibrarySectionSubItems(state, id),
  );
  const isCollapsed = useSelector((state) =>
    selectAseSetupLibrarySectionIsCollapsed(state, id),
  );

  if (!section) {
    return null;
  }

  const hasSubitems =
    !!subitems.questionIds.length || !!subitems.checklistIds.length;

  return (
    <Card
      borderRadiusBottom={isCollapsed || !hasSubitems}
      style={
        isCollapsed
          ? { marginBottom: theme.padding }
          : !hasSubitems
            ? { marginBottom: theme.padding / 2 }
            : { marginBottom: 1 }
      }
    >
      <CardRow
        icon={ICONS.ChevronRightLight}
        onIconPress={() => dispatch(REMOTE_ASESETUP_TOGGLESECTIONCOLLAPSED(id))}
        iconStyle={{
          transform: [{ rotate: isCollapsed ? '0deg' : '90deg' }],
        }}
        style={{ backgroundColor: '#ffffff80' }}
        onPress={() => dispatch(REMOTE_ASESETUP_SECTIONOPEN({ id }))}
        indentActions
        menuOptions={[
          {
            text: 'Open Section',
            icon: ICONS.CircleInfoLight,
            onPress: () => dispatch(REMOTE_ASESETUP_SECTIONOPEN({ id })),
          },

          {
            text: 'Reorder This',
            onPress: () =>
              dispatch(
                REMOTE_ASESETUP_OPENSECTIONSORDER({
                  folderId: section.folderId,
                }),
              ),
            icon: ICONS.GripLinesLight,
          },
          {
            text: 'Delete Section',
            icon: ICONS.TrashCanLight,
            destructive: true,
            onPress: () => dispatch(REMOTE_ASESETUP_SECTIONDELETE({ id })),
          },
          '-',
          {
            text: 'New Checklist',
            onPress: () =>
              dispatch(
                REMOTE_ASESETUPLIBRARY_QUESTIONOPEN({
                  sectionId: id,
                  type: AssessmentQuestionType.checklist,
                }),
              ),
            icon: ICONS.PlusLight,
          },
          {
            text: 'New Question',
            onPress: () =>
              dispatch(
                REMOTE_ASESETUPLIBRARY_QUESTIONOPEN({
                  sectionId: id,
                  type: AssessmentQuestionType.question,
                }),
              ),
            icon: ICONS.PlusLight,
          },
        ]}
      >
        <Txt size="small" weight="bold">
          {section.name}
        </Txt>
        <Txt size="xxxsmall" style={{ color: theme.colors.fieldValueText }}>
          {pluralise(
            subitems.questionIds.length,
            '1 Question',
            '{x} Questions',
            'No Questions',
          )}
          {' • '}
          {pluralise(
            subitems.checklistIds.length,
            '1 Checklist',
            '{x} Checklists',
            'No Checklists',
          )}
        </Txt>
      </CardRow>
    </Card>
  );
};
